import { useState, useCallback, useEffect } from 'react';
import { ReferralWithMetadata, ReferralFormData } from '../types/business';

interface UseReferralManagementOptions {
  businessId?: string;
  userType: 'business' | 'agent';
  userEmail?: string;
}

export function useReferralManagement({ 
  businessId, 
  userType, 
  userEmail 
}: UseReferralManagementOptions) {
  const [referrals, setReferrals] = useState<ReferralWithMetadata[]>([]);
  const [isLoadingReferrals, setIsLoadingReferrals] = useState<boolean>(false);
  const [referralsError, setReferralsError] = useState<string | null>(null);
  const [isAddingReferral, setIsAddingReferral] = useState<boolean>(false);
  const [isUpdatingReferral, setIsUpdatingReferral] = useState<boolean>(false);
  const [isDeletingReferral, setIsDeletingReferral] = useState<boolean>(false);

  const fetchReferrals = useCallback(async () => {
    if (!businessId) return;

    setIsLoadingReferrals(true);
    setReferralsError(null);
    
    try {
      // Build endpoint based on user type
      const endpoint = userType === 'business' 
        ? `/api/business/${businessId}/referrals`
        : `/api/business/${businessId}/${encodeURIComponent(userEmail!)}/referrals`;
        
      const response = await fetch(endpoint, {
        method: 'GET',
        credentials: 'same-origin'
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setReferrals(data.referrals || []);
      } else {
        setReferralsError(data.error || 'Failed to fetch referrals');
        setReferrals([]);
      }
    } catch (error) {
      console.error('Error fetching referrals:', error);
      setReferralsError(error instanceof Error ? error.message : 'An unexpected error occurred');
      setReferrals([]);
    } finally {
      setIsLoadingReferrals(false);
    }
  }, [businessId, userType, userEmail]);

  // Auto-fetch when dependencies change
  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  const addReferral = useCallback(async (referralData: ReferralFormData): Promise<{ success: boolean; error?: string }> => {
    if (!businessId) {
      return { success: false, error: 'No business ID available' };
    }

    setIsAddingReferral(true);
    
    try {
      const endpoint = userType === 'business' 
        ? `/api/business/${businessId}/referrals`
        : `/api/business/${businessId}/${encodeURIComponent(userEmail!)}/referrals`;
        
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(referralData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        await fetchReferrals(); // Refresh the list
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to add referral' };
      }
    } catch (error) {
      console.error('Error adding referral:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    } finally {
      setIsAddingReferral(false);
    }
  }, [businessId, userType, userEmail, fetchReferrals]);

  const updateReferral = useCallback(async (
    referralId: string, 
    updates: Partial<ReferralFormData>
  ): Promise<{ success: boolean; error?: string }> => {
    if (!businessId) {
      return { success: false, error: 'No business ID available' };
    }

    setIsUpdatingReferral(true);
    
    try {
      const endpoint = userType === 'business' 
        ? `/api/business/${businessId}/referrals`
        : `/api/business/${businessId}/${encodeURIComponent(userEmail!)}/referrals`;
        
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ referralId, updates })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        await fetchReferrals(); // Refresh the list
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to update referral' };
      }
    } catch (error) {
      console.error('Error updating referral:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    } finally {
      setIsUpdatingReferral(false);
    }
  }, [businessId, userType, userEmail, fetchReferrals]);

  const deleteReferral = useCallback(async (referralId: string): Promise<{ success: boolean; error?: string }> => {
    if (!businessId) {
      return { success: false, error: 'No business ID available' };
    }

    setIsDeletingReferral(true);
    
    try {
      const endpoint = userType === 'business' 
        ? `/api/business/${businessId}/referrals`
        : `/api/business/${businessId}/${encodeURIComponent(userEmail!)}/referrals`;
        
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ referralId })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        await fetchReferrals(); // Refresh the list
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to delete referral' };
      }
    } catch (error) {
      console.error('Error deleting referral:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    } finally {
      setIsDeletingReferral(false);
    }
  }, [businessId, userType, userEmail, fetchReferrals]);

  const refreshReferrals = fetchReferrals;

  return {
    referrals,
    isLoadingReferrals,
    referralsError,
    addReferral,
    updateReferral,
    deleteReferral,
    refreshReferrals,
    isAddingReferral,
    isUpdatingReferral,
    isDeletingReferral,
    fetchReferrals
  };
}